// Test component for email processing service
// Demonstrates the N8N webhook integration functionality

import React, { useState, useEffect } from 'react';
import { useEmailProcessing } from '@/hooks/email/useEmailProcessing';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Play, 
  RotateCcw, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertTriangle,
  Activity,
  RefreshCw
} from 'lucide-react';

export const EmailProcessingTest = () => {
  const { user } = useAuth();
  const {
    isProcessing,
    lastResult,
    error,
    webhookHealth,
    processHistorical,
    processDaily,
    retryProcessing,
    checkHealth,
    refreshToken,
    clearError,
    clearResult,
    reset
  } = useEmailProcessing();

  const [selectedType, setSelectedType] = useState('historical');
  const [batchId, setBatchId] = useState('');

  // Check webhook health on component mount
  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  const handleProcessEmails = async () => {
    if (!user?.id) {
      alert('Please log in to test email processing');
      return;
    }

    clearError();
    clearResult();

    if (selectedType === 'historical') {
      await processHistorical(user.id);
    } else {
      await processDaily(user.id);
    }
  };

  const handleRetry = async () => {
    if (!user?.id) {
      alert('Please log in to retry processing');
      return;
    }

    if (!lastResult?.batch_id) {
      alert('No previous processing result to retry');
      return;
    }

    clearError();
    await retryProcessing(user.id, selectedType);
  };

  const handleRefreshToken = async () => {
    if (!user?.id) {
      alert('Please log in to refresh token');
      return;
    }

    clearError();
    const refreshed = await refreshToken(user.id);
    alert(refreshed ? 'Token refreshed successfully' : 'Token refresh failed');
  };

  const handleCheckHealth = async () => {
    clearError();
    const isHealthy = await checkHealth();
    alert(isHealthy ? 'N8N webhook is healthy' : 'N8N webhook is not responding');
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Email Processing Test
        </h2>
        <p className="text-gray-600">
          Test the N8N webhook integration for camp email extraction
        </p>
      </div>

      {/* Webhook Health Status */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5" />
            <span className="font-medium">N8N Webhook Status</span>
          </div>
          <div className="flex items-center space-x-2">
            {webhookHealth === null ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
            ) : webhookHealth ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <span className={`text-sm font-medium ${
              webhookHealth === null ? 'text-gray-500' :
              webhookHealth ? 'text-green-600' : 'text-red-600'
            }`}>
              {webhookHealth === null ? 'Checking...' :
               webhookHealth ? 'Healthy' : 'Unhealthy'}
            </span>
            <button
              onClick={handleCheckHealth}
              className="p-1 hover:bg-gray-200 rounded"
              title="Check webhook health"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Processing Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Processing Type
        </label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="historical"
              checked={selectedType === 'historical'}
              onChange={(e) => setSelectedType(e.target.value)}
              className="mr-2"
            />
            <span>Historical (All emails)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="daily"
              checked={selectedType === 'daily'}
              onChange={(e) => setSelectedType(e.target.value)}
              className="mr-2"
            />
            <span>Daily (Recent emails)</span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={handleProcessEmails}
          disabled={isProcessing || !user?.id}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          <span>
            {isProcessing ? 'Processing...' : `Process ${selectedType} Emails`}
          </span>
        </button>

        <button
          onClick={handleRetry}
          disabled={isProcessing || !lastResult?.batch_id}
          className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Retry Processing</span>
        </button>

        <button
          onClick={handleRefreshToken}
          disabled={isProcessing || !user?.id}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh Token</span>
        </button>

        <button
          onClick={reset}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <XCircle className="w-4 h-4" />
          <span>Reset</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={clearError}
                className="text-xs text-red-600 hover:text-red-800 mt-2"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Display */}
      {lastResult && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Processing Result</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <div className="flex items-center space-x-2 mt-1">
                {lastResult.success ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <span className={`text-sm ${
                  lastResult.success ? 'text-green-600' : 'text-red-600'
                }`}>
                  {lastResult.success ? 'Success' : 'Failed'}
                </span>
              </div>
            </div>

            <div>
              <span className="text-sm font-medium text-gray-600">Type:</span>
              <span className="text-sm text-gray-900 ml-2 capitalize">
                {lastResult.processing_type}
              </span>
            </div>

            {lastResult.batch_id && (
              <div>
                <span className="text-sm font-medium text-gray-600">Batch ID:</span>
                <span className="text-sm text-gray-900 ml-2 font-mono">
                  {lastResult.batch_id}
                </span>
              </div>
            )}

            {lastResult.emails_processed !== undefined && (
              <div>
                <span className="text-sm font-medium text-gray-600">Emails Processed:</span>
                <span className="text-sm text-gray-900 ml-2">
                  {lastResult.emails_processed.toLocaleString()}
                </span>
              </div>
            )}

            {lastResult.emails_extracted !== undefined && (
              <div>
                <span className="text-sm font-medium text-gray-600">Emails Extracted:</span>
                <span className="text-sm text-gray-900 ml-2">
                  {lastResult.emails_extracted.toLocaleString()}
                </span>
              </div>
            )}

            {lastResult.started_at && (
              <div>
                <span className="text-sm font-medium text-gray-600">Started:</span>
                <span className="text-sm text-gray-900 ml-2">
                  {new Date(lastResult.started_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          <div className="mt-4">
            <span className="text-sm font-medium text-gray-600">Message:</span>
            <p className="text-sm text-gray-900 mt-1">{lastResult.message}</p>
          </div>

          {lastResult.error && (
            <div className="mt-4">
              <span className="text-sm font-medium text-gray-600">Error Code:</span>
              <span className="text-sm text-red-600 ml-2 font-mono">
                {lastResult.error}
              </span>
            </div>
          )}
        </div>
      )}

      {/* User Info */}
      <div className="text-sm text-gray-500">
        {user ? (
          <p>Logged in as: <span className="font-medium">{user.email}</span></p>
        ) : (
          <p>Please log in to test email processing</p>
        )}
      </div>
    </div>
  );
};
